import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { PrintOptions } from '@/types/printing';
import { getCommonPrintStyles, getHeaderHtml, getFooterHtml } from './printStyles';
import { format } from 'date-fns';
import { downloadAsJPG } from './printingBridge';

interface ChartDataItem {
  name: string;
  value: number;
}

/**
 * Prints a chart from report components - now exports as JPG
 * @param title Chart title
 * @param data Chart data
 * @param options Print options (format)
 */
export const printReportChart = async (
  title: string,
  data: ChartDataItem[],
  options: PrintOptions
): Promise<boolean> => {
  try {
    switch (options.format) {
      case 'xlsx':
        return exportChartToExcel(title, data);
      case 'pdf':
      case 'print':
      default:
        return await exportChartAsJPG(title, data);
    }
  } catch (error) {
    console.error(`Error printing ${title} chart:`, error);
    return false;
  }
};

/**
 * Prints all reports at once - now exports as JPG
 * @param programDistribution Program distribution data
 * @param monthlyEnrollments Monthly enrollments data 
 * @param feeDistribution Fee distribution data
 * @param options Print options
 */
export const printAllReports = async (
  programDistribution: ChartDataItem[],
  monthlyEnrollments: ChartDataItem[],
  feeDistribution: ChartDataItem[],
  options: PrintOptions
): Promise<boolean> => {
  try {
    if (options.format === 'xlsx') {
      const workbook = XLSX.utils.book_new();
      
      // Add program distribution sheet
      const programsData = [
        ["Program Distribution"],
        ["Generated on:", format(new Date(), 'PPP')],
        [""],
        ["Program", "Count"]
      ];
      
      programDistribution.forEach(item => {
        programsData.push([item.name, item.value.toString()]);
      });
      
      const programsWs = XLSX.utils.aoa_to_sheet(programsData);
      XLSX.utils.book_append_sheet(workbook, programsWs, "Program_Distribution");
      
      // Add monthly enrollments sheet
      const enrollmentsData = [
        ["Monthly Enrollments"],
        ["Generated on:", format(new Date(), 'PPP')],
        [""],
        ["Month", "Enrollments"]
      ];
      
      monthlyEnrollments.forEach(item => {
        enrollmentsData.push([item.name, item.value.toString()]);
      });
      
      const enrollmentsWs = XLSX.utils.aoa_to_sheet(enrollmentsData);
      XLSX.utils.book_append_sheet(workbook, enrollmentsWs, "Monthly_Enrollments");
      
      // Add fee distribution sheet
      const feeData = [
        ["Fee Distribution"],
        ["Generated on:", format(new Date(), 'PPP')],
        [""],
        ["Fee Range", "Count"]
      ];
      
      feeDistribution.forEach(item => {
        feeData.push([item.name, item.value.toString()]);
      });
      
      const feeWs = XLSX.utils.aoa_to_sheet(feeData);
      XLSX.utils.book_append_sheet(workbook, feeWs, "Fee_Distribution");
      
      // Generate and save the Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data_blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data_blob, `school_reports_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      
      return true;
    } else {
      // Export all reports as JPG instead of printing
      return await exportAllReportsAsJPG(programDistribution, monthlyEnrollments, feeDistribution);
    }
  } catch (error) {
    console.error('Error printing all reports:', error);
    return false;
  }
};

/**
 * Exports all reports as JPG
 */
const exportAllReportsAsJPG = async (
  programDistribution: ChartDataItem[],
  monthlyEnrollments: ChartDataItem[],
  feeDistribution: ChartDataItem[]
): Promise<boolean> => {
  try {
    const styles = getCommonPrintStyles();
    
    // Generate tables for each data set
    const programTable = generateTableHtml(programDistribution, "Program Distribution");
    const enrollmentTable = generateTableHtml(monthlyEnrollments, "Monthly Enrollments");
    const feeTable = generateTableHtml(feeDistribution, "Fee Distribution");
    
    const html = `
      <html>
        <head>
          <title>School Reports</title>
          <style>
            ${styles}
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              margin-bottom: 40px;
              page-break-inside: avoid;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f0f7f5;
              color: #3D7069;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .value-cell {
              text-align: right;
            }
            .chart-title {
              text-align: center;
              color: #3D7069;
              font-size: 1.4rem;
              margin: 30px 0 10px;
              font-weight: bold;
            }
            .report-section {
              page-break-after: always;
            }
            .report-section:last-child {
              page-break-after: avoid;
            }
          </style>
        </head>
        <body>
          ${getHeaderHtml("School Reports")}
          
          <div class="report-section">
            ${programTable}
          </div>
          
          <div class="report-section">
            ${enrollmentTable}
          </div>
          
          <div class="report-section">
            ${feeTable}
          </div>
          
          ${getFooterHtml('report')}
        </body>
      </html>
    `;
    
    // Export as JPG instead of opening print window
    await downloadAsJPG(html, "School_Reports");
    return true;
  } catch (error) {
    console.error('Error exporting all reports as JPG:', error);
    return false;
  }
};

// Helper function to generate HTML table for export view
const generateTableHtml = (data: ChartDataItem[], title: string): string => {
  // Calculate total
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  // Generate rows
  const rows = data.map(item => `
    <tr>
      <td>${item.name}</td>
      <td class="value-cell">${item.value}</td>
    </tr>
  `).join('');
  
  return `
    <div class="chart-title">${title}</div>
    <table>
      <thead>
        <tr>
          <th>Category</th>
          <th style="width: 120px;">Value</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
        <tr class="total-row">
          <td><strong>Total</strong></td>
          <td class="value-cell"><strong>${total}</strong></td>
        </tr>
      </tbody>
    </table>
  `;
};

/**
 * Exports chart data to Excel
 */
const exportChartToExcel = (title: string, data: ChartDataItem[]): boolean => {
  try {
    const workbook = XLSX.utils.book_new();
    
    // Create worksheet data with headers
    const wsData = [
      [title],
      ['Generated on:', format(new Date(), 'PPP')],
      [''],
      ['Category', 'Value']
    ];
    
    // Add data rows
    data.forEach(item => {
      wsData.push([item.name, item.value.toString()]);
    });
    
    // Add total
    const total = data.reduce((sum, item) => sum + item.value, 0);
    wsData.push(['']);
    wsData.push(['Total', total.toString()]);
    
    // Create worksheet and set column widths
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ width: 20 }, { width: 12 }];
    
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, ws, title.replace(/\s+/g, '_'));
    
    // Generate the file and trigger download
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data_blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data_blob, `${title.toLowerCase().replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    
    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return false;
  }
};

/**
 * Exports chart as JPG instead of opening print window
 */
const exportChartAsJPG = async (title: string, data: ChartDataItem[]): Promise<boolean> => {
  try {
    const styles = getCommonPrintStyles();
    
    // Generate table HTML from data
    const tableRows = data.map(item => `
      <tr>
        <td>${item.name}</td>
        <td class="value-cell">${item.value}</td>
      </tr>
    `).join('');
    
    // Calculate total for the bottom row
    const total = data.reduce((sum, item) => sum + item.value, 0);
    
    const html = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            ${styles}
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              margin-bottom: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f0f7f5;
              color: #3D7069;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .value-cell {
              text-align: right;
            }
            .total-row {
              font-weight: bold;
              background-color: #f0f7f5;
            }
            .chart-title {
              text-align: center;
              color: #3D7069;
              font-size: 1.2rem;
              margin-bottom: 10px;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          ${getHeaderHtml(title)}
          
          <div class="chart-title">${title}</div>
          
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th style="width: 120px;">Value</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
              <tr class="total-row">
                <td>Total</td>
                <td class="value-cell">${total}</td>
              </tr>
            </tbody>
          </table>
          
          ${getFooterHtml('report')}
        </body>
      </html>
    `;
    
    // Export as JPG instead of opening print window
    await downloadAsJPG(html, title);
    return true;
  } catch (error) {
    console.error('Error exporting chart as JPG:', error);
    return false;
  }
};
